'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { willCallbackRequestSchema, WillCallbackRequestData } from '@/lib/schema';
import { submitWillCallbackRequest } from '@/lib/api';
import { Input } from '@/app/components/ui/Input';
import { Button } from '@/app/components/ui/Button';
import { Label } from '@/app/components/ui/Label';
import { Textarea } from '@/app/components/ui/Textarea';
import { Alert } from '@/app/components/ui/Alert';
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import Link from 'next/link';
import { motion } from 'framer-motion';

interface WillCallbackFormProps {
  source?: string;
  onSuccess?: () => void;
  livesInEnglandWales?: boolean | null;
  assetsInUk?: boolean | null;
  ownsBusiness?: boolean | null;
}

export function WillCallbackForm({ 
  source = 'eligibility-checker', 
  onSuccess, 
  livesInEnglandWales,
  assetsInUk,
  ownsBusiness,
}: WillCallbackFormProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const getIneligibilityReasonSentence = () => {
    const reasons = [];
    if (livesInEnglandWales === false) reasons.push("you live outside of England and Wales");
    if (assetsInUk === false) reasons.push("you own assets outside of the UK");
    if (ownsBusiness === true) reasons.push("you own a business");
    
    if (reasons.length === 0) return "As your circumstances are complex, we recommend a quick call to see how we can help.";
    
    let joinedReasons = "";
    if (reasons.length === 1) {
      joinedReasons = reasons[0];
    } else if (reasons.length === 2) {
      joinedReasons = `${reasons[0]} and ${reasons[1]}`;
    } else {
      joinedReasons = `${reasons.slice(0, -1).join(', ')} and ${reasons[reasons.length - 1]}`;
    }
    
    return `As ${joinedReasons}, we recommend a quick call to see how we can help.`;
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<WillCallbackRequestData>({
    resolver: yupResolver(willCallbackRequestSchema) as any,
    defaultValues: {
      source,
      lives_in_england_wales: livesInEnglandWales,
      assets_in_uk: assetsInUk,
      owns_business: ownsBusiness,
    },
  });

  const onSubmit = async (data: WillCallbackRequestData) => {
    setSubmitError(null);
    try {
      await submitWillCallbackRequest({ ...data, source });
      setIsSuccess(true);
      reset();
      if (onSuccess) onSuccess();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Something went wrong');
    }
  };

  if (isSuccess) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="text-center"
      >
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 10 }}
          className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <CheckCircle2 className="w-8 h-8" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3 className="text-3xl font-display text-teal mb-2">Request Received</h3>
          <p className="text-muted-foreground mb-4">
            Thank you! We have received your callback request and will be in touch shortly.
          </p>
          <Link href="/">
            <Button 
              variant="outline" 
              className="bg-white hover:bg-muted text-foreground"
            >
              Back to Home
            </Button>
          </Link>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <div className="max-w-xl mx-auto">
      <div className="flex flex-col items-center text-center gap-3 mb-10 animate-fade-in">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
          Speak with an expert
        </h2>
        <p className="text-lg leading-relaxed text-muted-foreground">
          {getIneligibilityReasonSentence()}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 text-left">
        {submitError && (
          <Alert variant="error">
            {submitError}
          </Alert>
        )}

        {/* Hidden fields for eligibility answers */}
        <input type="hidden" value={String(livesInEnglandWales)} {...register('lives_in_england_wales')} />
        <input type="hidden" value={String(assetsInUk)} {...register('assets_in_uk')} />
        <input type="hidden" value={String(ownsBusiness)} {...register('owns_business')} />

        <div className="space-y-2">
          <Label htmlFor="name" className="text-base font-medium text-gray-700">Name</Label>
          <Input
            id="name"
            {...register('name')}
            aria-invalid={!!errors.name}
          />
          {errors.name && (
            <p className="text-sm text-destructive font-medium">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="text-base font-medium text-gray-700">Phone Number</Label>
          <Input
            id="phone"
            type="tel"
            {...register('phone')}
            aria-invalid={!!errors.phone}
          />
          {errors.phone && (
            <p className="text-sm text-destructive font-medium">{errors.phone.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-base font-medium text-gray-700">Email</Label>
          <Input
            id="email"
            type="email"
            {...register('email')}
            aria-invalid={!!errors.email}
          />
          {errors.email && (
            <p className="text-sm text-destructive font-medium">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="notes" className="text-base font-medium text-gray-700">Notes (Optional)</Label>
          <Textarea
            id="notes"
            {...register('notes')}
            className="min-h-[100px]"
          />
        </div>

        <Button 
          type="submit" 
          className="w-full" 
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending Request...' : 'Request Callback'}
        </Button>

        <div className="flex flex-col items-center gap-2 pt-6 border-t border-foreground/5">
          <div className="flex items-center gap-2 text-teal/60">
            <ShieldCheck className="w-5 h-5" />
            <span className="font-bold text-sm uppercase tracking-wider">Your Data is Secure</span>
          </div>
          <p className="text-xs text-muted-foreground text-center w-full leading-relaxed">
            We will never share your information with third parties and only use it to contact you about your Will.
          </p>
        </div>
      </form>
    </div>
  );
}
